/**
 * Persons (roles) — dev tooling config `agentic/dev/persons.yml`.
 *
 * An item may carry `assignee: <person-id>` in its front-matter. This module
 * parses the roster the console offers in the swipe-up assign sheet (focus
 * mode) and the assign row (detail view). The YAML file is the single source
 * of truth (validated by tools/check_items.py); the console never writes it —
 * add people by editing the file (template inside).
 */
import { readFileOrNull, type RepoConfig } from "./github";

export const PERSONS_PATH = "agentic/dev/persons.yml";

export interface Person {
  /** Stable id used in front-matter (`assignee:`). Never rename one. */
  id: string;
  name: string;
  role?: string;
  github?: string;
}

/** Fallback roster when persons.yml is missing/unreadable — the founder. */
export const DEFAULT_PERSONS: Person[] = [
  { id: "jsjs19xx", name: "Jan", role: "founder", github: "jsjs19xx" },
];

/**
 * Parse the `persons:` block (tolerant tiny line parser, LF-normalised —
 * Windows CRLF would break the `.*$` regexes).
 */
export function parsePersons(yamlText: string): Person[] {
  const lines = yamlText.replace(/\r\n?/g, "\n").split("\n");
  const start = lines.findIndex((l) => /^persons:\s*$/.test(l));
  if (start === -1) return [];
  const persons: Person[] = [];
  let current: Person | null = null;
  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^\S/.test(line)) break; // left the persons block (e.g. `default:`)
    // `- id: bob   # …` — strip a trailing comment before matching the entry.
    const entry = /^\s*-\s*(?:id:\s*)?([A-Za-z0-9_-]+)\s*$/.exec(line.replace(/\s+#.*$/, ""));
    if (entry) {
      current = { id: entry[1], name: entry[1] };
      persons.push(current);
      continue;
    }
    if (!current) continue;
    const m = /^\s+(id|name|role|github):\s*(.+?)\s*$/.exec(line);
    if (!m) continue;
    const value = m[2].replace(/\s+#.*$/, "").trim();
    if (!value) continue;
    if (m[1] === "id") current.id = value;
    else if (m[1] === "name") current.name = value;
    else if (m[1] === "role") current.role = value;
    else if (m[1] === "github") current.github = value;
  }
  return persons.filter((p) => p.id);
}

/** Display name for an assignee id (falls back to the id itself). */
export function personLabel(persons: Person[], id: string | undefined): string | undefined {
  if (!id) return undefined;
  return persons.find((p) => p.id === id)?.name ?? id;
}

/** Load the roster from the repo; falls back to DEFAULT_PERSONS. */
export async function loadPersons(cfg: RepoConfig): Promise<Person[]> {
  const file = await readFileOrNull(cfg, PERSONS_PATH);
  const parsed = file ? parsePersons(file.content) : [];
  return parsed.length > 0 ? parsed : DEFAULT_PERSONS;
}
